import { useQuery } from '@apollo/client/react';

import type {
  ListingTransactionPriceStatsQuery,
  ListingTransactionsQuery,
} from '@graphql/__generated__/graphql';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import {
  LISTING_BENCHMARK_SUBJECT,
  LISTING_TRANSACTIONS,
  LISTING_TRANSACTION_PRICE_STATS,
} from '@modules/listings/listingActivityQueries';
import { useLocale } from '@shared/hooks/locale';

const PRICE_STATS_COUNTRIES = ['CH', 'ES', 'FR', 'IT'] as const;

export type PriceEvolution = {
  rate: number;
  adjustedPrice: number;
};

export type TransactionRow =
  ListingTransactionsQuery['get_property_transactions_distance'][number] & {
    source: 'crm';
    priceEvolution: PriceEvolution | null;
  };

export type ListingTransactions =
  | { state: 'loading' }
  | { state: 'error' }
  | { state: 'unavailable' }
  | {
      state: 'ready';
      rows: TransactionRow[];
      medianPrice: number | null;
      medianPricePerSqm: number | null;
      priceStatsUnavailable: boolean;
    };

type PriceStatsPoint = { date: string; sale_price_m2: number };

const median = (values: number[]): number | null => {
  if (values.length === 0) {
    return null;
  }
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
};

const firstDayOfMonthUTC = (date: Date): string =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1))
    .toISOString()
    .slice(0, 10);

const findNearest = (
  series: PriceStatsPoint[],
  targetIso: string,
): PriceStatsPoint | null => {
  if (series.length === 0) {
    return null;
  }
  const target = new Date(targetIso).getTime();
  let nearest = series[0];
  let minDiff = Math.abs(new Date(series[0].date).getTime() - target);
  for (const point of series) {
    const diff = Math.abs(new Date(point.date).getTime() - target);
    if (diff < minDiff) {
      minDiff = diff;
      nearest = point;
    }
  }
  return nearest;
};

export const useListingTransactions = (lotId: string): ListingTransactions => {
  const { me } = useAppData();
  const { countryCode } = useLocale();
  const isTenantSpanish = me?.tenant?.country_code === 'ES';

  const {
    data: subjectData,
    loading: subjectLoading,
    error: subjectError,
  } = useQuery(LISTING_BENCHMARK_SUBJECT, { variables: { lotId } });

  const propertyId = subjectData?.lots_by_pk?.property?.id ?? null;

  const {
    data,
    loading,
    error: transactionsError,
  } = useQuery(LISTING_TRANSACTIONS, {
    skip: propertyId == null,
    variables: { target_property_id: propertyId ?? '' },
  });

  const transactionRows = (
    data?.get_property_transactions_distance ?? []
  ).filter(
    (
      row,
    ): row is ListingTransactionsQuery['get_property_transactions_distance'][number] =>
      row?.transaction != null,
  );

  const placeIds = Array.from(
    new Set(
      transactionRows
        .map(row => row.transaction?.locality_id)
        .filter((id): id is string => id != null),
    ),
  );

  const signatureDates = transactionRows
    .map(row => row.transaction?.signature_date)
    .filter((date): date is string => date != null);
  const dateMin =
    signatureDates.length > 0
      ? firstDayOfMonthUTC(
          new Date(
            signatureDates.reduce((min, date) => (date < min ? date : min)),
          ),
        )
      : '1970-01-01';

  const supportedCountry = (
    PRICE_STATS_COUNTRIES as readonly string[]
  ).includes(countryCode);
  const canQueryPriceStats = supportedCountry && placeIds.length > 0;

  const { data: priceStatsData, error: priceStatsError } = useQuery(
    LISTING_TRANSACTION_PRICE_STATS,
    {
      skip: !canQueryPriceStats,
      context: { clientName: 'scrapers' },
      variables: {
        placeIds,
        dateMin,
        ch: countryCode === 'CH',
        es: countryCode === 'ES',
        fr: countryCode === 'FR',
        it: countryCode === 'IT',
      },
    },
  );

  if (subjectLoading || (propertyId != null && loading)) {
    return { state: 'loading' };
  }

  if (subjectError != null || transactionsError != null) {
    return { state: 'error' };
  }

  if (propertyId == null) {
    return { state: 'unavailable' };
  }

  const seriesByPlace = new Map<string, PriceStatsPoint[]>();
  const statsRows: ReadonlyArray<{
    place_id: string;
    date: string;
    sale_price_m2: number;
  }> = getPriceStatsRows(priceStatsData);
  for (const point of statsRows) {
    const existing = seriesByPlace.get(point.place_id);
    const next = { date: point.date, sale_price_m2: point.sale_price_m2 };
    if (existing) {
      existing.push(next);
    } else {
      seriesByPlace.set(point.place_id, [next]);
    }
  }

  const todayIso = firstDayOfMonthUTC(new Date());

  const rows: TransactionRow[] = transactionRows.map(row => ({
    ...row,
    source: 'crm',
    priceEvolution: computePriceEvolution(row, seriesByPlace, todayIso),
  }));

  const priceValues = rows
    .map(row => row.transaction?.purchase_price)
    .filter((value): value is number => value != null && value > 0);
  const pricePerSqmValues = rows
    .map(row => {
      const tx = row.transaction;
      const surface = isTenantSpanish ? tx?.built_surface : tx?.living_surface;
      const price = tx?.purchase_price;
      return price != null && surface != null && surface > 0
        ? price / surface
        : null;
    })
    .filter((value): value is number => value != null && value > 0);

  return {
    state: 'ready',
    rows,
    medianPrice: median(priceValues),
    medianPricePerSqm: median(pricePerSqmValues),
    priceStatsUnavailable: priceStatsError != null,
  };
};

const getPriceStatsRows = (
  priceStatsData: ListingTransactionPriceStatsQuery | undefined,
): ReadonlyArray<{ place_id: string; date: string; sale_price_m2: number }> =>
  priceStatsData?.ch_price_stats_historical ??
  priceStatsData?.es_price_stats_historical ??
  priceStatsData?.fr_price_stats_historical ??
  priceStatsData?.it_price_stats_historical ??
  [];

const computePriceEvolution = (
  row: ListingTransactionsQuery['get_property_transactions_distance'][number],
  seriesByPlace: Map<string, PriceStatsPoint[]>,
  todayIso: string,
): PriceEvolution | null => {
  const placeId = row.transaction?.locality_id;
  const signatureDate = row.transaction?.signature_date;
  const price = row.transaction?.purchase_price;
  if (placeId == null || signatureDate == null || price == null) {
    return null;
  }

  const series = seriesByPlace.get(placeId);
  if (series == null || series.length < 2) {
    return null;
  }

  const txMonthIso = firstDayOfMonthUTC(new Date(signatureDate));
  const oldest = findNearest(
    series.filter(point => point.date >= txMonthIso),
    txMonthIso,
  );
  const newest = findNearest(series, todayIso);
  if (
    oldest == null ||
    newest == null ||
    oldest.sale_price_m2 <= 0 ||
    oldest.date === newest.date
  ) {
    return null;
  }

  const rate =
    (newest.sale_price_m2 - oldest.sale_price_m2) / oldest.sale_price_m2;

  return { rate, adjustedPrice: Math.round(price * (1 + rate)) };
};
